import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { getNavByRole } from "../config/navigation";
import { useNewInquiryCount } from "../hooks/useNewInquiryCount";
import "../pages/account/Dashboard.css";

// Icons
import setting_icon from "../Assets/settings.png";
import account_icon from "../Assets/accounts.png";
import logout_icon from "../Assets/logout.png";

interface Props {
  children: React.ReactNode;
  activeSection: string;
  onSectionChange: (key: string) => void;
}

const DashboardLayout: React.FC<Props> = ({
  children,
  activeSection,
  onSectionChange,
}) => {
  const { user, logout, loading, organizations, selectOrganization } =
    useAuth();
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openSettings, setOpenSettings] = useState(false);

  const navItems = getNavByRole(user?.role, user?.privileges);

  const organizationId = user?.activeOrganization?.organizationId;

  const { newCount, refresh: refreshInquiryCount } =
    useNewInquiryCount(organizationId);

  useEffect(() => {
    refreshInquiryCount();
  }, [organizationId, refreshInquiryCount]);

  // Close settings popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (
        !target.closest(".settings_btn_dash") &&
        !target.closest(".setting_popup")
      ) {
        setOpenSettings(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleSettingsClick = () => {
    setOpenSettings((prev) => !prev);
  };

  const handleLogout = async () => {
    setOpenSettings(false);
    await logout();
  };

  return (
    <div className="dashboard-layout">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ===================== SIDEBAR ===================== */}
      <aside className={`dashboard-sidebar ${sidebarOpen ? "open" : ""}`}>
        <div className="sidebar-header">
          <h1>
            {user?.role === "PlatformOwner" && !user.activeOrganization
              ? "Platform Admin"
              : user?.activeOrganization?.name ?? "Organization"}
          </h1>
          {user?.isPlatformOwner ? (
            <button
              className="org-switcher__button"
              onClick={() => navigate("/dashboard/org-select")}
            >
              <span className="org-switcher__label">
                {user.activeOrganization
                  ? "Switch organization"
                  : "Manage organization"}
              </span>
              <span className="org-switcher__value">
                {user.activeOrganization?.name ?? "Select Organization"}
              </span>
            </button>
          ) : (
            user &&
            organizations.length > 1 && (
              <label className="org-switcher">
                <span className="org-switcher__label">
                  Switch organization
                </span>
                <select
                  className="org-switcher__select"
                  value={user.activeOrganization?.organizationId ?? ""}
                  onChange={(e) => selectOrganization(e.target.value)}
                >
                  {organizations.map((org) => (
                    <option
                      key={org.organizationId ?? org.id}
                      value={org.organizationId ?? org.id}
                    >
                      {org.name}
                    </option>
                  ))}
                </select>
              </label>
            )
          )}
        </div>

        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <button
              key={item.key}
              className={activeSection === item.key ? "active" : ""}
              onClick={() => {
                onSectionChange(item.key);
                setSidebarOpen(false);
              }}
            >
              {/* You can replace this with real icons later */}
              <span style={{ width: 20, display: "inline-block" }}>•</span>
              {item.label}
              {item.key === "inquiries" && newCount > 0 && (
                <span
                  className="nav-badge"
                  title={`${newCount} new ${newCount === 1 ? "inquiry" : "inquiries"}`}
                >
                  {newCount}
                </span>
              )}
            </button>
          ))}
        </nav>
      </aside>

      {/* ===================== MAIN AREA ===================== */}
      <div className="dashboard-main">
        {/* Top Navbar */}
        <nav className="nav-bar-dash">
          <div className="nav-dash-left">
            <button
              className="menu-toggle"
              onClick={() => setSidebarOpen((prev) => !prev)}
            >
              ☰
            </button>
          </div>

          {/* Right side: Profile + Settings */}
          <div className="nav-dash-right">
            <div className="user_portion">
              <div className="user_identity">
                <span className="user_identity__name">
                  {user?.userName || "Admin"}
                </span>
                <span className="user_identity__email">{user?.email ?? ""}</span>
              </div>

              <button
                className="settings_btn_dash"
                onClick={handleSettingsClick}
              >
                <img
                  src={setting_icon}
                  alt="settings"
                  className="settings_img"
                />
              </button>
            </div>

            {/* Settings Dropdown */}
            {openSettings && (
              <div className="setting_popup">
                <div
                  className="with_setting_icons"
                  onClick={() => {
                    onSectionChange("security");
                    setOpenSettings(false);
                  }}
                >
                  <img src={account_icon} alt="" />
                  <p>Security</p>
                </div>

                <div
                  className="with_setting_icons danger"
                  onClick={handleLogout}
                >
                  <img src={logout_icon} alt="" />
                  <p>Logout</p>
                </div>
              </div>
            )}
          </div>
        </nav>

        {/* Page Content */}
        <main className="dashboard-content">{children}</main>
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div className="global-loading">
          <div className="spinner"></div>
        </div>
      )}
    </div>
  );
};

export default DashboardLayout;